import React, { useState, useEffect, useMemo } from 'react';
import styles from '../../styles/YearConverter.module.css';
import { convertJapaneseToWestern, convertWesternToJapanese, formatEraYear, getRelativeYear, getOverlapYearsData } from '../../utils/eraCalculations';

const eraList = [
  { key: 'reiwa', label: '令和' },
  { key: 'heisei', label: '平成' },
  { key: 'showa', label: '昭和' },
  { key: 'taisho', label: '大正' },
  { key: 'meiji', label: '明治' },
];

const minYear = 1868;
const maxYear = new Date().getFullYear() + 100;

// 年号変換のカスタムフック
function useYearConverter(yearSelection) {
  const [data, setData] = useState({
    western: '',
    era: 'reiwa',
    eraYear: '',
    relative: '',
    dualEra: null
  });
  
  // 重複年データをメモ化
  const overlapYears = useMemo(() => getOverlapYearsData(), []);
  
  // 西暦から和暦へ変換
  const convertWestern = (western) => {
    if (!western || isNaN(Number(western))) return null;
    
    const year = Number(western);
    const result = {
      western,
      relative: getRelativeYear(year, new Date().getFullYear())
    };
    
    if (year < minYear) return result;
    
    const { era: jpEra, eraYear: jpYear } = convertWesternToJapanese(year);
    if (!jpEra || !jpYear) return result;
    
    const eraItem = eraList.find(e => e.label === jpEra);
    if (!eraItem) return result;
    
    result.era = eraItem.key;
    result.eraYear = jpYear.toString();
    
    // 元号切り替わり年の処理
    if (overlapYears[year]) {
      const secondEraInfo = Object.entries(overlapYears[year])
        .find(([era]) => era !== jpEra);
      
      if (secondEraInfo) {
        const [secondEra, secondEraYear] = secondEraInfo;
        result.dualEra = { era: secondEra, eraYear: secondEraYear };
      }
    }
    
    return result;
  };
  
  // 和暦から西暦へ変換
  const convertJapanese = (eraKey, eraYear) => {
    if (!eraYear || isNaN(Number(eraYear))) return null;
    
    const year = Number(eraYear);
    const western = convertJapaneseToWestern(eraKey, year);
    
    if (western <= 0) return null;
    
    const result = {
      western: western.toString(),
      era: eraKey,
      eraYear: eraYear.toString(),
      relative: getRelativeYear(western, new Date().getFullYear())
    };
    
    // 元号切り替わり年の処理
    if (overlapYears[western]) {
      const currentEraLabel = eraList.find(e => e.key === eraKey)?.label;
      const secondEraInfo = Object.entries(overlapYears[western])
        .find(([era]) => era !== currentEraLabel);
      
      if (secondEraInfo) {
        const [secondEra, secondEraYear] = secondEraInfo;
        result.dualEra = { era: secondEra, eraYear: secondEraYear };
      }
    }
    
    return result;
  };
  
  // 西暦入力ハンドラ
  const handleWesternChange = (value) => {
    const result = convertWestern(value);
    setData({
      western: value,
      era: result?.era || '',
      eraYear: result?.eraYear || '',
      relative: result?.relative || '',
      dualEra: result?.dualEra || null
    });
  };
  
  // 元号選択ハンドラ
  const handleEraChange = (eraKey) => {
    const result = convertJapanese(eraKey, data.eraYear);
    setData({
      western: result?.western || '',
      era: eraKey,
      eraYear: data.eraYear,
      relative: result?.relative || '',
      dualEra: result?.dualEra || null
    });
  };
  
  // 和暦年入力ハンドラ
  const handleEraYearChange = (value) => {
    const result = convertJapanese(data.era, value);
    setData({
      western: result?.western || '',
      era: data.era,
      eraYear: value,
      relative: result?.relative || '',
      dualEra: result?.dualEra || null
    });
  };

  // 年数操作ボタンのハンドラ
  const handleYearOffset = (offset) => {
    const currentYear = new Date().getFullYear();
    const baseYear = data.western ? Number(data.western) : currentYear;
    const newYear = baseYear + offset;
    
    if (newYear >= minYear && newYear <= maxYear) {
      handleWesternChange(newYear.toString());
    }
  };
  
  // 表からの選択を処理
  useEffect(() => {
    if (!yearSelection) return;
    
    const newData = {
      western: yearSelection.westernYear.toString(),
      relative: getRelativeYear(yearSelection.westernYear, new Date().getFullYear()),
      era: '',
      eraYear: '',
      dualEra: null
    };
    
    // 元号情報のセット
    if (yearSelection.isOverlapYear) {
      const primaryEraItem = eraList.find(e => e.label === yearSelection.primaryEra);
      if (primaryEraItem) {
        newData.era = primaryEraItem.key;
        newData.eraYear = yearSelection.primaryEraYear.toString();
        newData.dualEra = {
          era: yearSelection.secondaryEra,
          eraYear: yearSelection.secondaryEraYear
        };
      }
    } else {
      const eraItem = eraList.find(e => e.label === yearSelection.primaryEra);
      if (eraItem) {
        newData.era = eraItem.key;
        newData.eraYear = yearSelection.primaryEraYear.toString();
      }
    }
    
    setData(newData);
  }, [yearSelection]);
  
  return {
    data,
    handleWesternChange,
    handleEraChange,
    handleEraYearChange,
    handleYearOffset
  };
}

const YearConverter = ({ yearSelection }) => {
  const { data, handleWesternChange, handleEraChange, handleEraYearChange, handleYearOffset } = 
    useYearConverter(yearSelection);
  
  // ヘルパー関数
  const getEraLabel = (eraKey) => {
    const item = eraList.find(e => e.key === eraKey);
    return item ? item.label : '';
  };

  return (
    <div className={styles.dualConvertContainer}>
      <div className={styles.dualInputRow}>
        {/* 西暦入力 */}
        <div className={styles.dualInputCol}>
          <label htmlFor="westernYear" className={styles.formLabel}>西暦</label>
          <div className={styles.inputWithControls}>
            <input
              type="number"
              id="westernYear"
              className={styles.inputField}
              value={data.western}
              onChange={(e) => handleWesternChange(e.target.value)}
              placeholder="2024"
              min={minYear}
              max={maxYear}
              inputMode="numeric"
              pattern="[0-9]*"
              style={{ width: '8em' }}
            />
            <span className={styles.yearLabel}>年</span>
            <div className={styles.miniControls}>
              <button 
                type="button"
                className={styles.miniButton}
                onClick={() => handleYearOffset(-3)}
                title="3年前"
              >
                -3
              </button>
              <button 
                type="button"
                className={styles.miniButton}
                onClick={() => handleYearOffset(-6)}
                title="6年前"
              >
                -6
              </button>
              <button 
                type="button"
                className={styles.miniButton}
                onClick={() => handleYearOffset(-10)}
                title="10年前"
              >
                -10
              </button>
              <button 
                type="button"
                className={styles.miniButton}
                onClick={() => handleYearOffset(3)}
                title="3年後"
              >
                +3
              </button>
              <button 
                type="button"
                className={styles.miniButton}
                onClick={() => handleYearOffset(6)}
                title="6年後"
              >
                +6
              </button>
              <button 
                type="button"
                className={styles.miniButton}
                onClick={() => handleYearOffset(10)}
                title="10年後"
              >
                +10
              </button>
            </div>
          </div>
        </div>
        {/* 和暦入力 */}
        <div className={styles.dualInputCol}>
          <label className={styles.formLabel}>和暦</label>
          <div className={styles.eraChipGroup}>
            {eraList.map((item) => (
              <button
                key={item.key}
                type="button"
                className={`${styles.eraChip} ${data.era === item.key ? styles.eraChipActive : ''}`}
                onClick={() => handleEraChange(item.key)}
              >
                {item.label}
              </button>
            ))}
          </div>
          <span className={styles.inputWithYear}>
            <input
              type="number"
              id="eraYear"
              className={styles.inputField}
              value={data.eraYear}
              onChange={(e) => handleEraYearChange(e.target.value)}
              placeholder="1"
              min="1"
              max="100"
              inputMode="numeric"
              pattern="[0-9]*"
              style={{ width: '6em' }}
            />
            <span className={styles.yearLabel}>年</span>
          </span>
        </div>
      </div>
      {/* 結果表示 */}
      <div className={styles.resultBox}>
        <div className={styles.resultColumn}>
          <div className={styles.resultLabel}>西暦</div>
          <div className={styles.resultValue}>{data.western ? `${data.western}年` : '-'}</div>
        </div>
        <div className={styles.resultColumn}>
          <div className={styles.resultLabel}>和暦</div>
          <div className={styles.resultValue}>
            {(data.western && Number(data.western) < minYear)
              ? '-'
              : (data.era && data.eraYear)
                ? (
                  <div className={styles.eraValueContainer}>
                    <div className={styles.primaryEra}>{getEraLabel(data.era)}{formatEraYear(Number(data.eraYear))}年</div>
                    {data.dualEra && <div className={styles.secondaryEra}>{data.dualEra.era}{formatEraYear(data.dualEra.eraYear)}年</div>}
                  </div>
                )
                : '-'
            }
          </div>
        </div>
        <div className={styles.resultColumn}>
          <div className={styles.resultLabel}>相対年</div>
          <div className={styles.resultValue}>{data.relative || '-'}</div>
        </div>
      </div>
    </div>
  );
};

export default YearConverter; 